import { Collection } from '@domain/product/models/Collection';
import { Resource } from '@domain/base/Resource';
import { CollectionDataSource } from '../datasources/CollectionDataSource';
import {
  CreateCollectionDTO,
  UpdateCollectionDTO,
} from '../dto/CollectionDTO';
import { CollectionMapper } from '../mappers/CollectionMapper';

export class CollectionRepository {
  constructor(private collectionDataSource: CollectionDataSource) {}

  async getCollections(): Promise<Resource<Collection[]>> {
    try {
      const response = await this.collectionDataSource.getCollections();
      return {
        status: 'success',
        data: response.map((dto) => CollectionMapper.toModel(dto)),
      };
    } catch {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to get collections' },
      };
    }
  }

  async getCollection(collectionId: string): Promise<Resource<Collection>> {
    try {
      const response = await this.collectionDataSource.getCollection(
        collectionId
      );
      return { status: 'success', data: CollectionMapper.toModel(response) };
    } catch {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to get collection' },
      };
    }
  }

  async createCollection(
    data: CreateCollectionDTO
  ): Promise<Resource<Collection>> {
    try {
      const response = await this.collectionDataSource.createCollection(data);
      return { status: 'success', data: CollectionMapper.toModel(response) };
    } catch {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to create collection' },
      };
    }
  }

  async updateCollection(
    collectionId: string,
    data: UpdateCollectionDTO
  ): Promise<Resource<Collection>> {
    try {
      const response = await this.collectionDataSource.updateCollection(
        collectionId,
        data
      );
      return { status: 'success', data: CollectionMapper.toModel(response) };
    } catch {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to update collection' },
      };
    }
  }

  async deleteCollection(collectionId: string): Promise<Resource<void>> {
    try {
      await this.collectionDataSource.deleteCollection(collectionId);
      return { status: 'success', data: undefined };
    } catch {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to delete collection' },
      };
    }
  }

  async addProductsToCollection(
    collectionId: string,
    productIds: string[]
  ): Promise<Resource<Collection>> {
    try {
      const response = await this.collectionDataSource.addProductsToCollection(
        collectionId,
        { productIds }
      );
      return { status: 'success', data: CollectionMapper.toModel(response) };
    } catch {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to add products to collection',
        },
      };
    }
  }

  async removeProductsFromCollection(
    collectionId: string,
    productIds: string[]
  ): Promise<Resource<void>> {
    try {
      await this.collectionDataSource.removeProductsFromCollection(
        collectionId,
        { productIds }
      );
      return { status: 'success', data: undefined };
    } catch {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to remove products from collection',
        },
      };
    }
  }
}
